import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { label, money } from "@/lib/constants";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Wrench, Trash2, Pencil, Wand2, ShieldAlert, Link2, BadgeCheck } from "lucide-react";

const blank = { name: "", company: "", trade: "cleaning", email: "", phone: "", pay_model: "per_job",
  rate: 0, insured: false, insurance_expires: "", notes: "", active: true };

export default function Vendors() {
  const { user } = useAuth();
  const canManage = can(user, "vendor:manage");
  const canPayout = can(user, "payout:manage");
  const [meta, setMeta] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    api.get("/vendors").then(({ data }) => setVendors(data));
    if (can(user, "payout:read")) api.get("/payouts").then(({ data }) => setPayouts(data));
  }, [user]);

  useEffect(() => {
    api.get("/vendors/meta").then(({ data }) => setMeta(data));
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, rate: Number(form.rate || 0),
      insurance_expires: form.insurance_expires || null };
    try {
      if (editing) await api.put(`/vendors/${editing}`, payload);
      else await api.post("/vendors", payload);
      toast.success(editing ? "Vendor updated" : "Vendor added");
      setOpen(false);
      setEditing(null);
      setForm(blank);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save vendor");
    }
    setBusy(false);
  };

  const remove = async (v) => {
    try {
      await api.delete(`/vendors/${v._id}`);
      toast.success(`${v.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove vendor");
    }
  };

  const runPayouts = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payouts/generate", {});
      toast.success(data.created
        ? `${data.created} payout(s) prepared for ${data.period_start} – ${data.period_end}`
        : "No unpaid completed work in that period");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not prepare payouts");
    }
    setBusy(false);
  };

  const pay = async (p, method) => {
    try {
      await api.post(`/payouts/${p._id}/pay`, { method });
      toast.success(`${p.payee_name} marked paid`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not settle payout");
    }
  };

  const connect = async (payeeId) => {
    try {
      const { data } = await api.post("/payouts/connect/onboarding",
        { payee_id: payeeId, origin_url: window.location.origin });
      window.location.href = data.onboarding_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payout setup");
    }
  };

  const checkConnect = async (payeeId, name) => {
    try {
      const { data } = await api.get(`/payouts/connect/${payeeId}/status`);
      toast[data.payouts_enabled ? "success" : "info"](
        data.payouts_enabled
          ? `${name} is ready to receive payouts`
          : data.connected
            ? `${name} still has ${data.requirements.length || "a few"} step(s) left with Stripe`
            : `${name} has not started payout setup yet`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not check payout status");
    }
  };

  return (
    <div className="space-y-8" data-testid="vendors-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Your bench</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Vendors & Payouts</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            The people who do the work, what they cost, and what you owe them.
          </p>
        </div>
        <div className="flex gap-2">
          {canPayout && (
            <Button data-testid="run-payouts-btn" variant="outline" disabled={busy} onClick={runPayouts}
              className="h-11 gap-2 border-gold text-royal-deep hover:bg-gold/10">
              <Wand2 className="h-4 w-4" /> Prepare payouts
            </Button>
          )}
          {canManage && (
            <Button data-testid="new-vendor-btn" onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
              className="bg-royal hover:bg-royal-deep text-white gap-2 h-11">
              <Plus className="h-4 w-4" /> Add vendor
            </Button>
          )}
        </div>
      </div>

      {vendors.length === 0 ? (
        <div className="rounded-lg border border-dashed border-royal/25 bg-white p-10 text-center">
          <Wrench className="h-7 w-7 text-gold mx-auto" />
          <p className="font-display text-lg font-semibold text-royal-deep mt-3">No vendors yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Add your handyman, plumber, pool service or linen supplier and assign them to work orders.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="vendors-list">
          {vendors.map((v) => (
            <div key={v._id} data-testid={`vendor-card-${v._id}`}
              className={`rounded-lg border bg-white p-5 hover:shadow-md transition-shadow duration-200 ${
                v.active ? "border-border" : "border-border opacity-70"}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-display font-semibold text-royal-deep truncate">{v.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {label(v.trade)}
                    {v.company ? ` · ${v.company}` : ""}
                  </p>
                </div>
                {v.insured ? (
                  <BadgeCheck className={`h-4 w-4 shrink-0 ${v.insurance_expired ? "text-red-600" : "text-emerald-600"}`} />
                ) : (
                  <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
                )}
              </div>
              <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                {v.email && <p className="truncate">{v.email}</p>}
                {v.phone && <p>{v.phone}</p>}
                <p className="text-royal-deep font-medium">
                  {money(v.rate)} · {label(v.pay_model)}
                </p>
                {v.open_work_orders > 0 && <p>{v.open_work_orders} open work order(s)</p>}
                {!v.insured && <p className="text-amber-700">No insurance on file</p>}
                {v.insurance_expired && <p className="text-red-700">Insurance expired {v.insurance_expires}</p>}
                {v.payouts_enabled ? (
                  <p className="flex items-center gap-1.5 text-emerald-700" data-testid={`vendor-payouts-ready-${v._id}`}>
                    <BadgeCheck className="h-3.5 w-3.5" /> Bank account connected
                  </p>
                ) : v.connected ? (
                  <p className="text-amber-700" data-testid={`vendor-payouts-pending-${v._id}`}>
                    Stripe payout setup unfinished
                  </p>
                ) : (
                  <p data-testid={`vendor-payouts-none-${v._id}`}>No bank account for payouts</p>
                )}
              </div>
              {canPayout && !v.payouts_enabled && (
                <div className="mt-3 flex gap-2">
                  <Button data-testid={`vendor-connect-${v._id}`} variant="outline" onClick={() => connect(v._id)}
                    className="flex-1 min-h-[40px] text-xs border-gold text-royal-deep hover:bg-gold/10 gap-1.5">
                    <Link2 className="h-3.5 w-3.5" /> {v.connected ? "Finish payout setup" : "Set up payouts"}
                  </Button>
                  {v.connected && (
                    <Button data-testid={`vendor-connect-check-${v._id}`} variant="outline"
                      onClick={() => checkConnect(v._id, v.name)}
                      className="min-h-[40px] px-3 text-xs border-royal/30 text-royal hover:bg-royal/5">
                      Check
                    </Button>
                  )}
                </div>
              )}
              {canManage && (
                <div className="mt-4 flex gap-2">
                  <Button data-testid={`edit-vendor-${v._id}`} variant="outline"
                    onClick={() => { setEditing(v._id); setForm({ ...blank, ...v, insurance_expires: v.insurance_expires || "" }); setOpen(true); }}
                    className="flex-1 min-h-[40px] text-xs border-royal/30 text-royal hover:bg-royal/5 gap-2">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button data-testid={`delete-vendor-${v._id}`} variant="outline" onClick={() => remove(v)}
                    className="flex-1 min-h-[40px] text-xs border-destructive/40 text-destructive hover:bg-destructive/5 gap-2">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {can(user, "payout:read") && (
        <section data-testid="payouts-section">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Payout runs</h2>
            <div className="h-px flex-1 bg-border" />
          </div>
          {payouts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No payouts prepared yet. "Prepare payouts" totals last week's completed work per person.
            </p>
          ) : (
            <div className="rounded-lg border border-border bg-white divide-y divide-border" data-testid="payouts-list">
              {payouts.map((p) => (
                <div key={p._id} className="px-4 sm:px-5 py-4" data-testid={`payout-${p._id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-display font-semibold text-royal-deep">{p.payee_name}</p>
                    <StatusBadge value={p.status === "paid" ? "paid" : "pending"} testId={`payout-status-${p._id}`} />
                    <span className="rounded-full border border-royal/15 bg-royal/5 px-2 py-0.5 text-[11px] text-royal">
                      {label(p.payee_kind)}
                    </span>
                    <p className="ml-auto font-display text-lg font-bold text-royal-deep">{money(p.amount)}</p>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {p.jobs} work order(s) · {p.period_start} – {p.period_end}
                    {p.paid_at ? ` · paid ${new Date(p.paid_at).toLocaleDateString()} via ${p.method}` : ""}
                  </p>
                  {canPayout && p.status !== "paid" && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button data-testid={`mark-paid-${p._id}`} variant="outline" onClick={() => pay(p, "manual")}
                        className="h-10 text-xs border-royal/30 text-royal hover:bg-royal/5">
                        Mark paid
                      </Button>
                      {p.payouts_enabled ? (
                        <Button data-testid={`stripe-pay-${p._id}`} onClick={() => pay(p, "stripe")}
                          className="h-10 text-xs bg-gold hover:bg-gold/90 text-royal-deep font-semibold gap-1.5">
                          Send via Stripe
                        </Button>
                      ) : (
                        <>
                          <Button data-testid={`connect-${p._id}`} variant="outline" onClick={() => connect(p.payee_id)}
                            className="h-10 text-xs border-gold text-royal-deep hover:bg-gold/10 gap-1.5">
                            <Link2 className="h-3.5 w-3.5" />
                            {p.connected ? "Finish Stripe payout setup" : "Set up Stripe payouts"}
                          </Button>
                          {p.connected && (
                            <Button data-testid={`connect-check-${p._id}`} variant="outline"
                              onClick={() => checkConnect(p.payee_id, p.payee_name)}
                              className="h-10 text-xs border-royal/30 text-royal hover:bg-royal/5">
                              Check status
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">
              {editing ? "Edit vendor" : "Add a vendor"}
            </DialogTitle>
            <DialogDescription>Who they are, what they charge, and whether they're insured.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="vendor-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input data-testid="vendor-name-input" required value={form.name}
                  onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company</Label>
                <Input data-testid="vendor-company-input" value={form.company}
                  onChange={(e) => set("company", e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Trade</Label>
                <Select value={form.trade} onValueChange={(v) => set("trade", v)}>
                  <SelectTrigger data-testid="vendor-trade-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.trades || []).map((t) => (
                      <SelectItem key={t} value={t} data-testid={`trade-${t}`}>{label(t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">How they're paid</Label>
                <Select value={form.pay_model} onValueChange={(v) => set("pay_model", v)}>
                  <SelectTrigger data-testid="vendor-pay-model-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.pay_models || []).map((m) => (
                      <SelectItem key={m.value} value={m.value} data-testid={`pay-model-${m.value}`}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rate ($)</Label>
                <Input data-testid="vendor-rate-input" type="number" min="0" step="0.01" value={form.rate}
                  onChange={(e) => set("rate", e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input data-testid="vendor-email-input" type="email" value={form.email}
                  onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                <Input data-testid="vendor-phone-input" value={form.phone}
                  onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Insurance expires</Label>
                <Input data-testid="vendor-insurance-input" type="date" value={form.insurance_expires}
                  onChange={(e) => set("insurance_expires", e.target.value)} className="mt-1.5 h-11" />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-royal-deep">
                <Switch data-testid="vendor-insured-switch" checked={form.insured}
                  onCheckedChange={(v) => set("insured", v)} />
                Insurance on file
              </label>
              <label className="flex items-center gap-2 text-sm text-royal-deep">
                <Switch data-testid="vendor-active-switch" checked={form.active}
                  onCheckedChange={(v) => set("active", v)} />
                Active
              </label>
            </div>
            <Button data-testid="save-vendor-btn" type="submit" disabled={busy}
              className="w-full h-12 bg-royal hover:bg-royal-deep text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save vendor" : "Add vendor"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
